/**
 * Timeclock (WP4-TIMECLOCK). Staff clock in and out from the top-bar button
 * (app/layout/TimeclockButton.tsx, `timeclock.use`); managers review and fix
 * hours here (`timeclock.manage`), manage PINs, PIN-only staff and the PMS
 * time clock mapping (/timesheets/staff), and anyone punches with a PIN on the
 * kiosk page (/timeclock/kiosk, `timeclock.use`). Query keys start with `timeclock`, the
 * entity the API publishes on every clock and edit.
 */
import { defineManifest } from '@velora/contracts';
import { TIMECLOCK_ENTITY, TIMECLOCK_PERMISSIONS } from '@velora/contracts/timeclock';
import { defineWebModule } from '../../app/module.js';

export default defineWebModule({
  manifest: defineManifest({
    id: 'timeclock',
    title: 'Timeclock',
    version: '0.3.0',
    owner: 'WP4-TIMECLOCK',
    entitlementKey: null,
    permissions: [TIMECLOCK_PERMISSIONS.use, TIMECLOCK_PERMISSIONS.manage],
    routes: [
      {
        id: 'timesheets',
        path: '/timesheets',
        title: 'Timesheets',
        officeScope: 'location',
        permission: TIMECLOCK_PERMISSIONS.manage,
      },
      {
        id: 'staff',
        path: '/timesheets/staff',
        title: 'Timeclock people',
        officeScope: 'location',
        permission: TIMECLOCK_PERMISSIONS.manage,
      },
      {
        // Shared front-desk PC: any staff member punches with their PIN.
        id: 'kiosk',
        path: '/timeclock/kiosk',
        title: 'Punch clock',
        officeScope: 'location',
        permission: TIMECLOCK_PERMISSIONS.use,
      },
    ],
    nav: [
      {
        label: 'Timesheets',
        group: 'operations',
        routeId: 'timesheets',
        icon: 'clock',
        order: 40,
        permission: TIMECLOCK_PERMISSIONS.manage,
      },
    ],
    commands: [
      {
        id: 'timeclock.timesheets',
        title: 'Timesheets',
        keywords: ['timeclock', 'hours', 'payroll', 'overtime', 'clock in', 'clock out', 'punch'],
        routeId: 'timesheets',
        permission: TIMECLOCK_PERMISSIONS.manage,
      },
      {
        id: 'timeclock.people',
        title: 'Timeclock people, PINs and PMS mapping',
        keywords: ['pin', 'badge', 'staff', 'dentrix', 'open dental', 'time clock', 'mapping'],
        routeId: 'staff',
        permission: TIMECLOCK_PERMISSIONS.manage,
      },
      {
        id: 'timeclock.kiosk',
        title: 'Punch clock (PIN kiosk)',
        keywords: ['pin', 'kiosk', 'punch', 'clock in', 'clock out'],
        routeId: 'kiosk',
        permission: TIMECLOCK_PERMISSIONS.use,
      },
    ],
    sseEntityKeys: [TIMECLOCK_ENTITY],
  }),
  pages: {
    timesheets: () => import('./TimesheetPage.js'),
    staff: () => import('./StaffPage.js'),
    kiosk: () => import('./KioskPage.js'),
  },
});
